import { UserSecretKey } from '@multiversx/sdk-core'
import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'

const safeWindow = typeof window !== 'undefined' ? window : undefined

// Derive the private key seed using HKDF (Web Crypto API)
export const derivePrivateKeySeed = async (
  prfOutput: Uint8Array,
): Promise<Uint8Array> => {
  if (!safeWindow) {
    throw new Error('Web Crypto API is not available')
  }

  // Import the PRF output as a CryptoKey
  const keyMaterial = await safeWindow.crypto.subtle.importKey(
    'raw', // format of the key material
    prfOutput, // the key material
    'HKDF', // HMAC-based Key Derivation Function
    false, // non-extractable
    ['deriveBits'], // keyUsages
  )

  //should be hardcoded in order to have deterministic output
  const salt = new Uint8Array([]) // Empty salt
  const info = new TextEncoder().encode('Ed25519 Key Generation')

  const derivedBitsBuffer = await safeWindow.crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: salt.buffer,
      info: info,
    },
    keyMaterial,
    256, // Length in bits
  )

  return new Uint8Array(derivedBitsBuffer)
}

// Generate the Ed25519 key pair
export const generateEd25519KeyPair = (privateKeySeed: Uint8Array) => {
  const privateKey = privateKeySeed
  ed.etc.sha512Sync = sha512
  const publicKey = ed.getPublicKey(privateKey)

  return {
    publicKey,
    privateKey,
  }
}

export const getKeypair = async (prfOutput: Uint8Array) => {
  const privateKeySeed = await derivePrivateKeySeed(prfOutput)
  const { privateKey } = generateEd25519KeyPair(privateKeySeed)

  const userSecretKey = new UserSecretKey(privateKey)
  const address = userSecretKey.generatePublicKey().toAddress()

  return {
    privateKey: userSecretKey.hex(),
    publicKey: address.toBech32(),
  }
}
